import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import SearchPage from './pages/SearchPage';
import ListingDetailPage from './pages/ListingDetailPage';
import StatesPage from './pages/StatesPage';
import ClaimListingPage from './pages/ClaimListingPage';
import ComparePage from './pages/ComparePage';
import AssistantPage from './pages/AssistantPage';
import SEOResourcePage from './pages/SEOResourcePage';
import SEOCategoryPage from './pages/SEOCategoryPage';
import AdvertisePage from './pages/AdvertisePage';
import AddListingPage from './pages/AddListingPage';
import ReviewVerifyPage from './pages/ReviewVerifyPage';
import AnalyticsConsent from './components/AnalyticsConsent';
import { initializeAnalytics, trackPageView } from './services/analytics';

const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

const Tree: React.FC<{ children: React.ReactNode }> = ({ children }) =>
  recaptchaSiteKey
    ? <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey} scriptProps={{ async: true, defer: true }}>{children}</GoogleReCaptchaProvider>
    : <>{children}</>;

const AnalyticsRouteTracker: React.FC = () => {
  const location = useLocation();

  React.useEffect(() => initializeAnalytics(), []);
  React.useEffect(() => trackPageView(location.pathname), [location.pathname]);

  return <AnalyticsConsent />;
};

const SeoRouteMetadata: React.FC = () => {
  const location = useLocation();

  React.useEffect(() => {
    const path = location.pathname;
    const canonicalPaths: Record<string, string> = {
      '/': '/',
      '/advertise': '/advertise',
    };
    const canonicalPath = canonicalPaths[path];
    let canonical = document.querySelector<HTMLLinkElement>('link[data-trr-canonical]');
    if (canonicalPath) {
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        canonical.dataset.trrCanonical = 'true';
        document.head.appendChild(canonical);
      }
      canonical.href = `${window.location.origin}${canonicalPath}`;
    } else {
      canonical?.remove();
    }

    const indexable = path === '/' || path === '/advertise';
    let robots = document.querySelector<HTMLMetaElement>('meta[data-trr-robots]');
    if (!indexable) {
      if (!robots) {
        robots = document.createElement('meta');
        robots.name = 'robots';
        robots.dataset.trrRobots = 'true';
        document.head.appendChild(robots);
      }
      robots.content = 'noindex,follow';
    } else {
      robots?.remove();
    }

    let structuredData = document.querySelector<HTMLScriptElement>('script[data-trr-website-schema]');
    if (path === '/') {
      if (!structuredData) {
        structuredData = document.createElement('script');
        structuredData.type = 'application/ld+json';
        structuredData.dataset.trrWebsiteSchema = 'true';
        document.head.appendChild(structuredData);
      }
      structuredData.text = JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': [
          { '@type': 'Organization', name: 'TopRatedRehabs', url: 'https://topratedrehabs.com/' },
          { '@type': 'WebSite', name: 'TopRatedRehabs', url: 'https://topratedrehabs.com/' },
        ],
      });
    } else {
      structuredData?.remove();
    }
  }, [location.pathname]);

  return null;
};

const App: React.FC = () => {
  return (
    <Tree>
    <BrowserRouter>
      <AnalyticsRouteTracker />
      <SeoRouteMetadata />
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}>
        <Header />
        <main style={{ flex: 1 }}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/listing/:id" element={<ListingDetailPage />} />
            <Route path="/states" element={<StatesPage />} />
            <Route path="/claim/:listingId" element={<ClaimListingPage />} />
            <Route path="/resources" element={<SEOResourcePage />} />
            <Route path="/resources/" element={<SEOResourcePage />} />
            <Route path="/resources/c/:category" element={<SEOCategoryPage />} />
            <Route path="/advertise" element={<AdvertisePage />} />
            <Route path="/add-listing" element={<AddListingPage />} />
            <Route path="/compare" element={<ComparePage />} />
            <Route path="/assistant" element={<AssistantPage />} />
            <Route path="/review/verify/:token" element={<ReviewVerifyPage />} />
            <Route path="*" element={<div style={{ padding: '60px', textAlign: 'center' }}><h2>404 - Page Not Found</h2></div>} />
          </Routes>
        </main>
        <Footer />
      </div>
    </BrowserRouter>
    </Tree>
  );
};

export default App;
